import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from './config';
import logger from './utils/logger';
import { FiUser, FiBriefcase, FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight } from "react-icons/fi";
import { GoogleLogin } from '@react-oauth/google';
import './App.css';

function SignupPage() {
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // UI States
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  // Helper function to handle successful signup/login response
  const processLoginSuccess = (response) => {
    const accessToken = response.data.accessToken;
    const refreshToken = response.data.refreshToken;
    const user = response.data.user;
    const orgId = user?.companyId;

    if (!accessToken || !refreshToken) {
      setErrorMessage('Signup failed: Invalid server response');
      setIsLoading(false);
      return;
    }

    localStorage.setItem('authToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('orgId', orgId || '');
    localStorage.setItem('email', user.email);
    localStorage.setItem('userName', user.name || user.email.split('@')[0] || 'User');
    localStorage.setItem('roles', JSON.stringify(user.roles || []));
    localStorage.setItem('userId', user.id || '');

    navigate('/dashboard');
  };

  const handleSignup = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    logger.auth.info('Signup attempt', { email, company: companyName });

    axios.post(`${API_BASE_URL}/auth/signup`, {
      email,
      password,
      companyName,
      fullName
    })
    .then(response => {
      logger.auth.info('Signup successful', { email });
      processLoginSuccess(response);
    })
    .catch(error => {
      logger.auth.error('Signup failed', { email, error: error.response?.data?.error || error.message });
      const msg = error.response?.data?.error || 'Signup failed. Please try again.';
      setErrorMessage(msg);
      setIsLoading(false);
    });
  };

  // Google SSO Handler
  const handleGoogleSuccess = (credentialResponse) => {
    setIsLoading(true);
    setErrorMessage('');

    axios.post(`${API_BASE_URL}/auth/google-login`, {
      token: credentialResponse.credential
    })
    .then(response => {
      processLoginSuccess(response);
    })
    .catch(error => {
      logger.auth.error('Google SSO signup failed', { error: error.response?.data?.error || error.message });
      const msg = error.response?.data?.error || 'Google Sign-In failed. Please try again.';
      setErrorMessage(msg);
      setIsLoading(false);
    });
  };

  // --- SIGNTADA SPECIFIC STYLES FOR SIGNUP PAGE ---
  const styles = `
    .signup-container {
      display: flex;
      height: 100vh;
      width: 100%;
      overflow: hidden;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      box-sizing: border-box;
    }

    .signup-container * {
      box-sizing: border-box;
    }

    /* Left Side - Branding */
    .signup-left {
      flex: 1;
      background: #0c0c0c;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 40px;
      position: relative;
    }

    .signup-left::before {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: linear-gradient(135deg, rgba(255, 214, 0, 0.08) 0%, rgba(255, 214, 0, 0.03) 50%, transparent 100%);
      opacity: 1;
    }

    .signup-left::after {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background-image:
        linear-gradient(45deg, rgba(255, 214, 0, 0.03) 25%, transparent 25%),
        linear-gradient(-45deg, rgba(255, 214, 0, 0.03) 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, rgba(255, 214, 0, 0.03) 75%),
        linear-gradient(-45deg, transparent 75%, rgba(255, 214, 0, 0.03) 75%);
      background-size: 60px 60px;
      background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
      opacity: 0.4;
    }

    .brand-content {
      position: relative;
      z-index: 2;
      max-width: 400px;
    }

    .brand-title {
      font-size: 48px;
      font-weight: 800;
      margin-bottom: 10px;
      letter-spacing: -0.5px;
    }

    .brand-tagline {
      font-size: 18px;
      color: #fafafa;
      font-weight: 500;
      margin-bottom: 20px;
    }

    .brand-divider {
      width: 60px;
      height: 4px;
      background: #FFD600;
      margin: 0 auto 20px;
      border-radius: 2px;
    }

    .brand-subtext {
      font-size: 14px;
      color: #a3a3a3;
      font-weight: 400;
    }

    /* Right Side - Form */
    .signup-right {
      flex: 1;
      background: #0c0c0c;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
    }

    .signup-box {
      width: 100%;
      max-width: 420px;
    }

    .signup-header {
      margin-bottom: 30px;
      text-align: left;
    }

    .signup-header h2 {
      font-size: 28px;
      font-weight: 700;
      color: #fafafa;
      margin-bottom: 8px;
    }

    .text-muted {
      color: #a3a3a3;
      font-size: 14px;
    }

    .signup-form {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .form-group label {
      font-size: 14px;
      font-weight: 600;
      color: #d4d4d4;
    }

    .input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .signup-form .input-icon {
      position: absolute;
      left: 16px;
      color: #a3a3a3;
      font-size: 20px;
      z-index: 10;
      pointer-events: none;
    }

    .signup-form .form-input {
      width: 100%;
      height: 48px;
      padding: 10px 16px 10px 48px !important;
      border: 1px solid #2a2a2a;
      border-radius: 8px;
      font-size: 15px;
      color: #fafafa;
      background-color: #141414;
      transition: all 0.2s ease;
      outline: none;
    }

    .signup-form .form-input::placeholder {
      color: #6b7280;
    }

    .signup-form .form-input:focus {
      border-color: #FFD600;
      background-color: #1a1a1a;
      box-shadow: 0 0 0 4px rgba(255, 214, 0, 0.15);
    }

    .password-toggle {
      position: absolute;
      right: 16px;
      background: none;
      border: none;
      cursor: pointer;
      color: #a3a3a3;
      font-size: 20px;
      display: flex;
      align-items: center;
      padding: 0;
      z-index: 10;
    }

    .password-toggle:hover {
      color: #d4d4d4;
    }

    .btn-signup {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      width: 100%;
      height: 48px;
      background: #FFD600;
      color: black;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      transition: background 0.2s;
      margin-top: 10px;
    }

    .btn-signup:hover {
      background: #FFEA00;
      transform: translateY(-1px);
      box-shadow: 0 4px 6px rgba(0,0,0,0.2);
    }

    .btn-signup:disabled {
      background: #ffe59e;
      cursor: not-allowed;
    }

    .loader-spinner {
      width: 20px;
      height: 20px;
      border: 2px solid #000;
      border-bottom-color: transparent;
      border-radius: 50%;
      display: inline-block;
      box-sizing: border-box;
      animation: rotation 1s linear infinite;
    }

    @keyframes rotation {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .alert-error-box {
      background-color: rgba(239, 68, 68, 0.2);
      border: 1px solid rgba(239, 68, 68, 0.4);
      color: #fca5a5;
      padding: 12px;
      border-radius: 8px;
      font-size: 14px;
      text-align: center;
    }

    .sso-divider {
      display: flex;
      align-items: center;
      text-align: center;
      margin: 20px 0;
      color: #a3a3a3;
      font-size: 14px;
    }

    .sso-divider::before,
    .sso-divider::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid #2a2a2a;
    }

    .sso-divider::before {
      margin-right: 10px;
    }

    .sso-divider::after {
      margin-left: 10px;
    }

    .google-login-wrapper {
      display: flex;
      justify-content: center;
      width: 100%;
    }

    .signin-link {
      text-align: center;
      margin-top: 20px;
    }

    .signin-link a {
      color: #fbbf24;
      text-decoration: none;
      font-weight: 600;
      font-size: 14px;
    }

    .signin-link a:hover {
      text-decoration: underline;
    }

    .signup-footer {
      margin-top: 40px;
      text-align: center;
    }

    .signup-footer p {
      color: #737373;
      font-size: 12px;
    }

    /* Mobile Responsiveness */
    @media (max-width: 768px) {
      .signup-container {
        flex-direction: column;
      }

      .signup-left {
        display: none;
      }

      .signup-right {
        padding: 20px;
      }
    }
  `;

  return (
    <div className="signup-container">
      <style>{styles}</style>

      {/* LEFT SIDE - BRANDING */}
      <div className="signup-left">
        <div className="brand-content">
          <h1 className="brand-title"><span style={{color: '#fafafa'}}>Sign</span><span style={{background: 'linear-gradient(135deg, #FFD600 0%, #FFAB00 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>Tada</span></h1>
          <p className="brand-tagline">Start Your Free Trial</p>
          <div className="brand-divider"></div>
          <p className="brand-subtext">Secure. Legally Binding. Fast.</p>
        </div>
      </div>

      {/* RIGHT SIDE - FORM */}
      <div className="signup-right">
        <div className="signup-box">
          <div className="signup-header">
            <h2>Create Your Account</h2>
            <p className="text-muted">Get started with your free SignTada account.</p>
          </div>

          <form onSubmit={handleSignup} className="signup-form">

            {/* Full Name Input */}
            <div className="form-group">
              <label>Full Name</label>
              <div className="input-wrapper">
                <FiUser className="input-icon" />
                <input
                  type="text"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="form-input"
                />
              </div>
            </div>

            {/* Company Name Input */}
            <div className="form-group">
              <label>Company Name</label>
              <div className="input-wrapper">
                <FiBriefcase className="input-icon" />
                <input
                  type="text"
                  placeholder="Enter your company name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="form-input"
                />
              </div>
            </div>

            {/* Email Input */}
            <div className="form-group">
              <label>Email Address</label>
              <div className="input-wrapper">
                <FiMail className="input-icon" />
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="form-input"
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="form-group">
              <label>Password</label>
              <div className="input-wrapper">
                <FiLock className="input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="form-input"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="alert-error-box">
                {errorMessage}
              </div>
            )}

            {/* Submit Button */}
            <button type="submit" className="btn-signup" disabled={isLoading}>
              {isLoading ? (
                <span className="loader-spinner"></span>
              ) : (
                <>Create Account <FiArrowRight /></>
              )}
            </button>
          </form>

          {/* Google SSO Section */}
          <div className="sso-divider">
            <span>OR</span>
          </div>

          <div className="google-login-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => {
                setErrorMessage('Google Sign-In failed');
              }}
              theme="outline"
              size="large"
              width="100%"
              text="signup_with"
            />
          </div>

          {/* Sign In Link */}
          <div className="signin-link">
            <span style={{color: '#a3a3a3', fontSize: '14px'}}>Already have an account? </span>
            <Link to="/login">Sign In</Link>
          </div>

          <div className="signup-footer">
            <p>&copy; {new Date().getFullYear()} SignTada Inc.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
